import { useRef,useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../utils/api';
import BackButton from '../../components/BackButton';
import { Mic } from 'lucide-react';

const ShiftNote = () => {
  const { id } = useParams(); // shiftId
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [isRecording, setIsRecording] = useState(false);
  const [shift, setShift] = useState(null);
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState(null);
  const [existingPhoto, setExistingPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [noteId, setNoteId] = useState(null);
  const fileInputRef = useRef(null);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('aiHelpEnabled');
    if (saved !== null) {
      setAiEnabled(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('aiHelpEnabled', JSON.stringify(aiEnabled));
  }, [aiEnabled]);

  useEffect(() => {
    const fetchShift = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/shifts/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setShift(res.data);
      } catch (err) {
        toast.error('Error loading shift');
      }
    };

    const fetchNote = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/notes/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data) {
          setNote(res.data.content);
          setExistingPhoto(res.data.photo || null);
          setEditing(true);
          setAiEnabled(false);
          setNoteId(res.data.id);
        }
      } catch (err) {
        toast.error("Error loading notes");
      }
    };

    fetchShift();
    fetchNote();
  }, [id, token]);

  const triggerAIGeneration = (input) => {
    if (!aiEnabled || (editing && input.trim().length === 0)) return;

    if (typingTimeout) clearTimeout(typingTimeout);

    const timeout = setTimeout(() => {
      if (input.length >= 10 && shift) {
        generateShiftNote(input);
      }
    }, 2000);

    setTypingTimeout(timeout);
  };

  const handleInputChange = (e) => {
    const input = e.target.value;
    setNote(input);
    triggerAIGeneration(input);
  };

  const generateShiftNote = async (inputText) => {
    try {
      setIsGenerating(true);
      const res = await axios.post(`${API_BASE_URL}/api/notes/generate-draft`, {
        input: inputText,
        clientId: shift.clientId,
        date: shift.date,
        startTime: shift.startTime,
        endTime: shift.endTime
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.data?.draft) {
        setNote(res.data.draft);
      }
    } catch (err) {
      toast.error("Generation error");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData();
    formData.append('content', note);
    formData.append('clientId', shift.Client?.id);
    if (photo) formData.append('photo', photo);

    try {
      const url = editing
        ? `${API_BASE_URL}/api/notes/${noteId}`
        : `${API_BASE_URL}/api/notes/${id}`;
      const method = editing ? 'put' : 'post';

      await axios[method](url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Note saved successfully!');
      navigate('/shifts');
    } catch (err) {
      toast.error('Error saving note');
    } finally {
      setLoading(false);
    }
  };

  const startSpeechRecognition = () => {
    if (!('webkitSpeechRecognition' in window)) {
      toast.error('Speech recognition not supported in this browser.');
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => setIsRecording(true);
    recognition.onerror = () => setIsRecording(false);
    recognition.onend = () => setIsRecording(false);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setNote(prev => {
        const updatedNote = prev + ' ' + transcript;
        triggerAIGeneration(updatedNote);
        return updatedNote;
      });
    };

    recognition.start();
  };

  return (
    <div className="min-h-screen bg-[#0E0A1F] text-white p-6">
      <BackButton />
      <div className="max-w-2xl mx-auto bg-[#1C1235] p-6 rounded-xl shadow space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold">{editing ? 'Edit Note' : 'New Shift Note'}</h2>
        </div>

        {shift && (
          <div className="bg-[#2A1C4A] p-4 rounded text-sm">
            <p><span className="text-gray-400">Shift info:</span> {shift.title}</p>
            <p><span className="text-gray-400">Client:</span> {shift.Client?.name}</p>
            <p><span className="text-gray-400">Date:</span> {shift.date}</p>
            <p><span className="text-gray-400">Time:</span> {dayjs(`${shift.date}T${shift.startTime}`).format('HH:mm')} - {dayjs(`${shift.date}T${shift.endTime}`).format('HH:mm')}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block mb-1">Shift Note
            <button
                type="button"
                onClick={startSpeechRecognition}
                className="right-3 text-white hover:text-purple-300"
                title="Speak your note"
                style={{float:"right"}}
              >
                <Mic className={`w-5 h-5 ${isRecording ? 'animate-pulse text-green-400' : ''}`} />
              </button>
            </label>
            <div className="relative">
            <textarea
                  value={note}
                  onChange={handleInputChange}
                  className="w-full h-60 p-3 rounded bg-[#2A1C4A] text-white placeholder-gray-400 font-mono"
                  placeholder={`Need help writing compliant notes?\n• Do not use participant's full name. First name only’\n• Use initials or ‘the participant’\n• Describe what happened, not what you think\n• Avoid personal opinions or assumptions\n• Don’t include private health info unless essential to the shift`}
                  required
                  disabled={isGenerating}
                />

              {isGenerating && (
                <div className="absolute top-2 right-10 text-purple-300 animate-pulse text-lg font-bold">
                  ...
                </div>
              )}
              
            </div>
            <p className="text-[11px] text-gray-400 leading-snug mt-1">
              All shift notes are reviewed and confirmed by the worker before saving. Sparks Scribe supports NDIS compliance and Australian Privacy Principles by not storing data with AI providers or sharing client records.
            </p>
          </div>

          <div className="flex justify-between items-end gap-4">
            <div className="lg:[display:contents]">
              <span>
                <label className="block mb-1">Upload Photo (optional)</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setPhoto(file);
                    }
                  }}
                  className="hidden"
                  id="photo-upload"
                />
                <label
                  htmlFor="photo-upload"
                  className="inline-block cursor-pointer px-4 py-2 bg-white text-black rounded hover:bg-blue-700"
                >
                  Choose Pic
                </label>

                {/* New Photo Preview */}
                {photo && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-400">Selected Photo:</p>
                    <img
                      src={URL.createObjectURL(photo)}
                      alt="Preview"
                      className="w-24 h-24 object-cover border rounded"
                    />
                  </div>
                )}

                {/* Existing Photo Display (only if no new photo selected) */}
                {existingPhoto && !photo && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-400">Current Photo:</p>
                    <img
                      src={`${API_BASE_URL}${existingPhoto}`}
                      alt="Existing"
                      className="w-24 h-24 object-cover border rounded"
                    />
                  </div>
                )}
              </span>

              <span>
              <label className="flex items-center gap-2 text-xs mb-2 mt-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={aiEnabled}
                  disabled={editing && note.trim().length === 0}
                  onClick={() => {
                    if (!editing || note.trim().length > 0) {
                      setAiEnabled(!aiEnabled);
                    }
                  }}
                  className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                    aiEnabled ? 'bg-green-500' : 'bg-gray-400'
                  } ${editing && note.trim().length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-300 ${
                      aiEnabled ? 'translate-x-4' : 'translate-x-1'
                    }`}
                  />
                </button>
                AI Assist
              </label>
              </span>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#8F4EFF] hover:bg-purple-700 py-3 rounded font-semibold"
          >
            {loading ? 'Saving...' : editing ? 'Update Note' : 'Save Note'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ShiftNote;
